import React, { useState, useRef, useEffect, useMemo } from 'react';
import Icon from '@/components/AppIcon';
import EmployeeAvatar from '@/components/employees/EmployeeAvatar';
import Image from '@/components/AppImage';
import Button from '@/components/ui/Button';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';

const MessagePanel = ({
  conversation,
  messages,
  onSendMessage,
  currentUser,
  typingUsers = [],
  className = '',
  onBack = null,
  showBackButton = false,
  onDeleteConversation = null,
  onSeeSharedDocuments = null,
  onSearchInConversation = null,
}) => {
  const { t, language } = useTranslation(['internalMessaging', 'common']);
  const translate = (key, fallback) => t(`internalMessaging:${key}`, fallback);
  const locale = language || 'en-US';
  const [messageText, setMessageText] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const actionsMenuRef = useRef(null);
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);

  const emojis = ['😊', '😂', '❤️', '👍', '👎', '😢', '😮', '😡', '🎉', '🔥', '💯', '👏'];

  const rootClasses = ['flex-1 flex flex-col bg-background', className]
    .filter(Boolean)
    .join(' ');

  const normalizedCurrentUserIds = useMemo(() => {
    const ids = new Set();
    const register = (value) => {
      if (value == null) {
        return;
      }

      const normalized = String(value).trim();
      if (normalized) {
        ids.add(normalized);
      }
    };

    register(currentUser?.id);
    register(currentUser?._id);
    register(currentUser?.userId);
    register(currentUser?.sub);
    register(currentUser?.user_id);

    if (Array.isArray(currentUser?.identifiers)) {
      currentUser.identifiers.forEach(register);
    }

    return Array.from(ids);
  }, [currentUser]);

  const currentUserIdSet = useMemo(() => new Set(normalizedCurrentUserIds), [normalizedCurrentUserIds]);

  const otherParticipantNames = useMemo(() => {
    if (!Array.isArray(conversation?.participants)) {
      return [];
    }

    const names = [];

    const resolveParticipantId = (participant) => {
      if (participant && typeof participant === 'object') {
        return (
          participant?.id
          ?? participant?._id
          ?? participant?.userId
          ?? null
        );
      }
      return participant ?? null;
    };

    const resolveParticipantObject = (participant) => (
      participant && typeof participant === 'object' ? participant : {}
    );

    for (const participant of conversation.participants) {
      const participantId = resolveParticipantId(participant);
      if (
        participantId
        && currentUserIdSet.has(String(participantId))
      ) {
        continue;
      }

      const participantObject = resolveParticipantObject(participant);
      const candidateName = (
        participantObject?.fullName
        ?? participantObject?.name
        ?? participantObject?.email
        ?? (typeof participant === 'string' ? participant : null)
      );

      if (candidateName && !names.includes(candidateName)) {
        names.push(candidateName);
      }
    }

    return names;
  }, [conversation?.participants, currentUserIdSet]);

  const conversationHeaderTitle = (() => {
    if (conversation?.type === 'announcement') {
      return (
        conversation?.name
        ?? translate('messagePanel.conversationFallback', 'Conversation')
      );
    }

    if (otherParticipantNames.length > 0) {
      return otherParticipantNames.join(', ');
    }

    return (
      conversation?.name
      ?? translate('messagePanel.conversationFallback', 'Conversation')
    );
  })();

  const handleBackClick = () => {
    if (typeof onBack === 'function') {
      onBack();
    }
  };

  const renderBackButton = () => {
    if (!showBackButton || typeof onBack !== 'function') {
      return null;
    }

    return (
      <button
        type="button"
        onClick={handleBackClick}
        className="lg:hidden inline-flex items-center justify-center rounded-full p-2 text-muted-foreground hover:text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
        aria-label={translate('messagePanel.backAria', 'Back to conversations')}
      >
        <Icon name="ArrowLeft" size={20} />
      </button>
    );
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    setSendError(null);
    setIsSending(false);
  }, [conversation?.id]);

  useEffect(() => {
    setIsActionsMenuOpen(false);
  }, [conversation?.id]);

  useEffect(() => {
    if (!isActionsMenuOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (!actionsMenuRef.current?.contains(event.target)) {
        setIsActionsMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event?.key === 'Escape') {
        setIsActionsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActionsMenuOpen]);

  useEffect(() => {
    if (sendError && messageText) {
      setSendError(null);
    }
  }, [messageText, sendError]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (isSending || !messageText?.trim()) {
      return;
    }

    if (!conversation?.id) {
      setSendError(translate('errors.send.noConversation', 'Select a conversation to continue.'));
      return;
    }

    setIsSending(true);
    setSendError(null);

    try {
      await onSendMessage?.({
        type: 'text',
        content: messageText.trim(),
        conversationId: conversation.id,
      });
      setMessageText('');
    } catch (error) {
      const responseMessage = error?.response?.data?.message;
      const message = Array.isArray(responseMessage)
        ? responseMessage.join('. ')
        : responseMessage
          ?? error?.message
          ?? translate('errors.send.failed', 'Unable to send message.');
      setSendError(message);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage().catch(() => {});
    }
  };

  const handleFileUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !conversation?.id || isSending) {
      return;
    }

    setIsSending(true);
    setSendError(null);

    try {
      await onSendMessage?.({
        type: 'file',
        content: file.name,
        fileSize: file.size,
        file,
        conversationId: conversation.id,
      });
    } catch (error) {
      const responseMessage = error?.response?.data?.message;
      const message = Array.isArray(responseMessage)
        ? responseMessage.join('. ')
        : responseMessage
          ?? error?.message
          ?? translate('errors.send.attachmentFailed', 'Unable to send attachment.');
      setSendError(message);
    } finally {
      setIsSending(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleEmojiSelect = (emoji) => {
    setMessageText(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        hour: 'numeric',
        minute: '2-digit',
      }),
    [locale],
  );

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return timeFormatter.format(date);
  };

  const sizeUnits = useMemo(
    () => [
      translate('fileSize.units.bytes', 'Bytes'),
      translate('fileSize.units.kb', 'KB'),
      translate('fileSize.units.mb', 'MB'),
      translate('fileSize.units.gb', 'GB'),
    ],
    [translate],
  );

  const formatFileSize = (bytes) => {
    if (!Number.isFinite(bytes) || bytes <= 0) {
      return `0 ${sizeUnits[0]}`;
    }
    const k = 1024;
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizeUnits.length - 1);
    const value = bytes / Math.pow(k, i);
    const formatted = new Intl.NumberFormat(locale, {
      maximumFractionDigits: 2,
    }).format(value);
    return `${formatted} ${sizeUnits[i]}`;
  };

  const sanitizedPhoneNumber = (() => {
    const resolveString = (value) => {
      if (typeof value !== 'string') {
        return null;
      }
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : null;
    };

    let rawValue = resolveString(conversation?.phoneNumber);

    if (!rawValue) {
      rawValue = resolveString(conversation?.metadata?.phoneNumber)
        ?? resolveString(conversation?.metadata?.phone)
        ?? resolveString(conversation?.metadata?.contactPhone)
        ?? resolveString(conversation?.metadata?.contact?.phone)
        ?? null;
    }

    if (!rawValue && Array.isArray(conversation?.participants)) {
      for (const participant of conversation.participants) {
        if (participant && typeof participant === 'object') {
          const candidate = resolveString(participant?.phone)
            ?? resolveString(participant?.phoneNumber)
            ?? resolveString(participant?.contactPhone)
            ?? resolveString(participant?.contact?.phone);
          if (candidate) {
            rawValue = candidate;
            break;
          }
        }
      }
    }

    if (typeof rawValue !== 'string') {
      return null;
    }

    const cleaned = rawValue.replace(/[^+\d]/g, '');
    return cleaned.length > 0 ? cleaned : null;
  })();

  const phoneHref = sanitizedPhoneNumber ? `tel:${sanitizedPhoneNumber}` : null;

  if (!conversation) {
    return (
      <div className={rootClasses}>
        <div className="p-4 border-b border-border bg-card">
          <div className="flex items-center space-x-3">
            {renderBackButton()}
            <h3 className="text-lg font-medium text-foreground">
              {translate('messagePanel.headerTitle', 'Messages')}
            </h3>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center px-6">
            <Icon name="MessageSquare" size={64} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              {translate('messagePanel.emptyHeading', 'Select a conversation')}
            </h3>
            <p className="text-muted-foreground">
              {translate('messagePanel.emptyBody', 'Choose a conversation from the list to start messaging')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={rootClasses}>
      {/* Header */}
      <div className="p-4 border-b border-border bg-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {renderBackButton()}
            {/* Avatar */}
            <div className="relative">
              {conversation?.type === 'group' ? (
                <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
                  <Icon name="Users" size={18} color="white" />
                </div>
              ) : conversation?.type === 'announcement' ? (
                <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
                  <Icon name="Megaphone" size={18} color="white" />
                </div>
              ) : (
                <EmployeeAvatar
                  url={conversation?.avatarUrl}
                  name={conversation?.name}
                  alt={`${conversation?.name ?? translate('messagePanel.conversationFallback', 'Conversation')} avatar`}
                  size="sm"
                  showStatus={false}
                />
              )}
              
              {conversation?.type === 'direct' && conversation?.isOnline && (
                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-success rounded-full border-2 border-card"></div>
              )}
            </div>

            {/* Info */}
            <div>
              <h3 className="font-medium text-foreground">{conversationHeaderTitle}</h3>
              <p className="text-sm text-muted-foreground">
                {conversation?.type === 'group'
                  && formatTranslation(
                    translate('messagePanel.membersLabel', '{{count}} members'),
                    { count: conversation?.memberCount ?? 0 },
                  )}
                {conversation?.type === 'direct'
                  && (conversation?.isOnline
                    ? translate('messagePanel.online', 'Online')
                    : translate('messagePanel.offline', 'Offline'))}
                {conversation?.type === 'announcement'
                  && translate('messagePanel.announcementLabel', 'Company Announcements')}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            {phoneHref ? (
              <Button
                asChild
                variant="ghost"
                className="p-2"
                aria-label={formatTranslation(
                  translate('messagePanel.callAria', 'Call {{name}}'),
                  { name: conversation?.name ?? translate('messagePanel.conversationFallback', 'Conversation') },
                )}
              >
                <a href={phoneHref}>
                  <Icon name="Phone" size={18} />
                </a>
              </Button>
            ) : (
              <Button
                variant="ghost"
                className="p-2"
                disabled
                aria-label={translate('messagePanel.callUnavailable', 'Call unavailable')}
              >
                <Icon name="Phone" size={18} />
              </Button>
            )}
            <div className="relative" ref={actionsMenuRef}>
              <Button
                variant="ghost"
                className="p-2"
                onClick={() => setIsActionsMenuOpen((prev) => !prev)}
                aria-haspopup="menu"
                aria-expanded={isActionsMenuOpen}
                aria-label={translate('messagePanel.actions.openMenu', 'Open conversation actions')}
              >
                <Icon name="MoreVertical" size={18} />
              </Button>
              {isActionsMenuOpen && (
                <div
                  className="absolute right-0 mt-2 w-52 bg-popover border border-border rounded-md shadow-elevation-2 py-1 z-50"
                  role="menu"
                >
                  <button
                    type="button"
                    className="w-full px-4 py-2 text-left text-sm hover:bg-muted focus:outline-none focus:bg-muted"
                    role="menuitem"
                    onClick={() => {
                      setIsActionsMenuOpen(false);
                      if (typeof onDeleteConversation === 'function') {
                        onDeleteConversation(conversation);
                      }
                    }}
                  >
                    {translate('messagePanel.actions.deleteConversation', 'Delete conversation')}
                  </button>
                  <button
                    type="button"
                    className="w-full px-4 py-2 text-left text-sm hover:bg-muted focus:outline-none focus:bg-muted"
                    role="menuitem"
                    onClick={() => {
                      setIsActionsMenuOpen(false);
                      if (typeof onSeeSharedDocuments === 'function') {
                        onSeeSharedDocuments(conversation);
                      }
                    }}
                  >
                    {translate('messagePanel.actions.seeSharedDocuments', 'See shared documents')}
                  </button>
                  <button
                    type="button"
                    className="w-full px-4 py-2 text-left text-sm hover:bg-muted focus:outline-none focus:bg-muted"
                    role="menuitem"
                    onClick={() => {
                      setIsActionsMenuOpen(false);
                      if (typeof onSearchInConversation === 'function') {
                        onSearchInConversation(conversation);
                      }
                    }}
                  >
                    {translate('messagePanel.actions.searchInConversation', 'Search in conversation')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages?.map((message) => {
          const messageSenderId = (() => {
            const candidates = [
              message?.senderId,
              message?.sender?.id,
              message?.sender?._id,
              message?.sender?.userId,
              message?.sender?.user_id,
            ];

            const firstValidId = candidates.find((candidate) => candidate != null);
            return firstValidId != null ? String(firstValidId) : null;
          })();

          const isOwnMessage = Boolean(messageSenderId) && currentUserIdSet.has(messageSenderId);
          const isUnseen = !isOwnMessage && !message?.seenAt;
          const attachment =
            message?.attachments && typeof message.attachments === 'object'
              ? message.attachments
              : null;
          const attachmentUrl = (() => {
            if (!attachment) {
              return null;
            }

            const candidate = attachment.url ?? null;
            if (typeof candidate !== 'string') {
              return null;
            }

            const trimmed = candidate.trim();
            return trimmed.length > 0 ? trimmed : null;
          })();
          const attachmentName = (() => {
            if (attachment && typeof attachment.originalName === 'string') {
              const trimmed = attachment.originalName.trim();
              if (trimmed.length > 0) {
                return trimmed;
              }
            }

            if (typeof message?.content === 'string' && message.content.trim().length > 0) {
              return message.content;
            }

            return translate('messagePanel.attachmentFallback', 'Attachment');
          })();
          const attachmentSize = (() => {
            const candidate = attachment?.size ?? message?.fileSize ?? null;
            if (typeof candidate !== 'number') {
              return null;
            }

            return Number.isFinite(candidate) && candidate >= 0 ? candidate : null;
          })();

          return (
            <div
              key={message?.id}
              className={`flex w-full ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`flex items-end space-x-2 max-w-[85%] sm:max-w-md ${
                  isOwnMessage ? 'flex-row-reverse space-x-reverse' : ''
                }`}
              >
                {/* Avatar for other users */}
                {!isOwnMessage && (
                  <EmployeeAvatar
                    url={message?.senderAvatarUrl}
                    name={message?.senderName}
                    alt={`${message?.senderName ?? translate('messagePanel.userFallback', 'User')} avatar`}
                    size="xs"
                    showStatus={false}
                  />
                )}

                {/* Message Content */}
                <div className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                  {/* Sender Name (for group chats) */}
                  {!isOwnMessage && conversation?.type === 'group' && (
                    <span className="text-xs text-muted-foreground mb-1 px-3">
                      {message?.senderName}
                    </span>
                  )}

                  {/* Message Bubble */}
                  <div
                    className={`px-4 py-2 rounded-2xl ${
                      isOwnMessage
                        ? 'bg-primary text-primary-foreground rounded-br-md'
                        : 'bg-muted text-foreground rounded-bl-md'
                    } ${isUnseen ? 'ring-2 ring-offset-2 ring-primary/50 font-semibold shadow-sm' : ''}`}
                    aria-label={
                      isUnseen
                        ? translate('messagePanel.unseenAria', 'Unseen message')
                        : undefined
                    }
                  >
                    {message?.type === 'text' && (
                      <p className="text-sm whitespace-pre-wrap">{message?.content}</p>
                    )}

                    {message?.type === 'file' && (
                      <div className="flex items-center space-x-2">
                        <Icon name="Paperclip" size={16} />
                        <div>
                          {attachmentUrl ? (
                            <a
                              href={attachmentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium underline underline-offset-2"
                            >
                              {attachmentName}
                            </a>
                          ) : (
                            <p className="text-sm font-medium">{attachmentName}</p>
                          )}
                          {attachmentSize !== null ? (
                            <p className="text-xs opacity-75">{formatFileSize(attachmentSize)}</p>
                          ) : null}
                        </div>
                      </div>
                    )}

                    {message?.type === 'image' && (
                      <div>
                        <Image
                          src={message?.imageUrl}
                          alt={message?.imageAlt}
                          className="max-w-full h-auto rounded-lg mb-2"
                        />
                        {message?.content && (
                          <p className="text-sm">{message?.content}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Timestamp and Status */}
                  <div className={`flex items-center space-x-1 mt-1 px-3 ${isOwnMessage ? 'flex-row-reverse space-x-reverse' : ''}`}>
                    <span className="text-xs text-muted-foreground">
                      {formatMessageTime(message?.timestamp)}
                    </span>
                    {isOwnMessage && (
                      <Icon 
                        name={message?.status === 'read' ? 'CheckCheck' : 'Check'} 
                        size={12} 
                        className={message?.status === 'read' ? 'text-primary' : 'text-muted-foreground'} 
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Typing Indicator */}
        {typingUsers?.length > 0 && (
          <div className="flex items-center space-x-2">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
            <span className="text-sm text-muted-foreground">
              {formatTranslation(
                translate(
                  `messagePanel.typing.${typingUsers?.length === 1 ? 'singular' : 'plural'}`,
                  typingUsers?.length === 1 ? '{{names}} is typing…' : '{{names}} are typing…',
                ),
                { names: typingUsers?.join(', ') },
              )}
            </span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
      {/* Message Input */}
      <div className="p-4 border-t border-border bg-card">
        <div className="flex items-end space-x-2">
          {/* File Upload */}
          <Button
            variant="ghost"
            onClick={() => fileInputRef?.current?.click()}
            className="p-2 flex-shrink-0"
            disabled={isSending}
          >
            <Icon name="Paperclip" size={18} />
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileUpload}
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.txt"
          />

          {/* Message Input */}
          <div className="flex-1 relative">
            <textarea
              value={messageText}
              onChange={(e) => setMessageText(e?.target?.value)}
              onKeyPress={handleKeyPress}
              placeholder={translate('messagePanel.inputPlaceholder', 'Type a message...')}
              rows={1}
              className="w-full px-4 py-2 pr-12 bg-input border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              style={{ minHeight: '40px', maxHeight: '120px' }}
              disabled={isSending}
            />
            
            {/* Emoji Button */}
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
              <Button
                variant="ghost"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="p-1"
              >
                <Icon name="Smile" size={16} />
              </Button>
            </div>

            {/* Emoji Picker */}
            {showEmojiPicker && (
              <div className="absolute bottom-full right-0 mb-2 bg-popover border border-border rounded-lg shadow-elevation-2 p-2 grid grid-cols-6 gap-1 z-50">
                {emojis?.map((emoji, index) => (
                  <button
                    key={index}
                    onClick={() => handleEmojiSelect(emoji)}
                    className="p-2 hover:bg-muted rounded text-lg transition-hover"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Send Button */}
          <Button
            onClick={() => handleSendMessage().catch(() => {})}
            disabled={!messageText?.trim() || isSending}
            className="p-2 flex-shrink-0"
          >
            {isSending ? (
              <Icon name="Loader2" size={18} className="animate-spin" />
            ) : (
              <Icon name="Send" size={18} />
            )}
          </Button>
        </div>

        {sendError && (
          <div className="mt-2 text-sm text-error" role="alert">
            {sendError}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagePanel;
